import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, SectionHead, Btn } from "../Bits";
import { SPORTS } from "../../lib/site";
import { IMAGES } from "../../lib/images";

const Sports = () => {
  const [active, setActive] = useState(0);
  const sport = SPORTS[active];

  return (
    <section
      data-testid="home-sports"
      className="border-y border-[#111111]/10 bg-[#F5F5F2] py-20 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <SectionHead
            eyebrow="Sport applications"
            title="One surface does not fit every sport"
            copy="Select a discipline to see the surface systems we would usually recommend and what to consider in the room."
          />
        </Reveal>

        <Reveal delay={0.08}>
          <div
            className="mt-12 flex flex-wrap gap-2.5 lg:mt-16"
            data-testid="sport-selector"
            role="tablist"
          >
            {SPORTS.map((s, i) => (
              <button
                key={s.name}
                role="tab"
                aria-selected={i === active}
                data-testid={`sport-tab-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(i)}
                className={`border px-5 py-3 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  i === active
                    ? "border-[#111111] bg-[#111111] text-white"
                    : "border-[#111111]/15 bg-white text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 border-t border-[#111111]/10 pt-12 lg:grid-cols-12 lg:gap-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={sport.name}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7"
              data-testid="sport-panel"
            >
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-[#111111] sm:text-3xl">
                {sport.name}
              </h3>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#3B3B3B] sm:text-base">
                {sport.copy}
              </p>

              <div className="mt-10 grid gap-10 sm:grid-cols-2">
                <div>
                  <p className="mako-label text-[#7A7F85]">Key surface considerations</p>
                  <ul className="mt-5 space-y-3">
                    {sport.considerations.map((c) => (
                      <li key={c} className="flex gap-3 text-sm text-[#3B3B3B]">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" strokeWidth={2} />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="mako-label text-[#7A7F85]">Recommended</p>
                  <ul className="mt-5 space-y-2">
                    {sport.recommended.map((r) => (
                      <li
                        key={r}
                        className="border-l-2 border-[#0F4C81] pl-4 font-display text-sm font-semibold text-[#111111]"
                      >
                        {r}
                      </li>
                    ))}
                  </ul>
                  <p className="mako-label mt-8 text-[#7A7F85]">Wall padding</p>
                  <p className="mt-4 text-sm leading-relaxed text-[#3B3B3B]">{sport.walls}</p>
                </div>
              </div>

              <div className="mt-11">
                <Btn to="/contact" data-testid="sport-discuss-cta">
                  Discuss your facility
                </Btn>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={sport.image_key}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="aspect-[4/5] overflow-hidden bg-white"
              >
                <img
                  src={IMAGES[sport.image_key]}
                  alt={`${sport.name} training surface`}
                  className="h-full w-full object-cover"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Sports;
